// src/pages/Services.jsx
import ViewfinderFrame from "../components/ViewfinderFrame";
import CtaFinal from "../components/CtaFinal";
import usePageTitle from "../hooks/usePageTitle";
import { SITE, whatsappLink } from "../data/site";
import "./Services.css";

const PACOTES = [
  {
    nome: "Aéreo",
    preco: "R$ 390",
    destaque: false,
    itens: ["Vídeo aéreo editado (45–60s)", "12 fotos aéreas tratadas", "Entrega em 48h"],
    apoio: "Para quando o diferencial do imóvel é a localização, a vista ou o terreno.",
    botao: "Contratar Aéreo",
    msg: "Olá! Quero contratar o pacote Aéreo da D-Rec.",
  },
  {
    nome: "Imóvel",
    preco: "R$ 690",
    destaque: true,
    itens: [
      "Tomadas aéreas com drone",
      "25 fotos (aéreas, internas e fachada)",
      "Tour em vídeo pelos ambientes (até 90s)",
      "Vídeo pronto para portal e Instagram",
      "Entrega em 48h",
    ],
    apoio:
      "O pacote completo do anúncio: drone e fotógrafo na mesma visita, sem contratar dois fornecedores.",
    botao: "Contratar Imóvel",
    msg: "Olá! Quero contratar o pacote Imóvel da D-Rec.",
  },
  {
    nome: "Lançamento",
    preco: "R$ 1.190",
    destaque: false,
    itens: [
      "Tudo do pacote Imóvel",
      "40 fotos tratadas",
      "Vídeo em dois formatos: horizontal (site/YouTube) e vertical (Reels/TikTok)",
      "Fotos de golden hour incluídas",
      "Entrega em 72h",
    ],
    apoio: "Para empreendimentos, alto padrão e campanhas de lançamento.",
    botao: "Contratar Lançamento",
    msg: "Olá! Quero contratar o pacote Lançamento da D-Rec.",
  },
];

const EXTRAS = [
  { nome: "Foto adicional", preco: "R$ 15 por foto" },
  { nome: "Entrega em 24h", preco: "R$ 150 (sujeito à agenda)" },
  { nome: "Fotos de golden hour", preco: "R$ 180" },
  { nome: "Deslocamento fora da RMR", preco: "R$ 1,50 por km (ida e volta) + diária de R$ 250" },
];

const FAQ = [
  {
    q: "O corretor precisa estar no imóvel?",
    a: "Alguém precisa abrir o imóvel para a equipe. Pode ser o corretor, o proprietário ou o porteiro com autorização.",
  },
  {
    q: "Quanto tempo dura a captação?",
    a: "Em média entre 1 e 2 horas, dependendo do tamanho do imóvel. Confirmamos a estimativa no agendamento.",
  },
  {
    q: "Como recebo o material?",
    a: "Por link de download, organizado por formato: fotos, vídeo horizontal e vídeo vertical.",
  },
  {
    q: "Posso pedir ajustes?",
    a: "Sim. Cada pacote inclui uma rodada de pequenos ajustes na edição (cor, corte, ordem das fotos), solicitada em até 7 dias após a entrega. Nova captação ou mudança de escopo são orçadas à parte.",
  },
  {
    q: "O que preciso preparar no imóvel?",
    a: "Recomendamos luzes acesas, cortinas abertas e ambientes organizados. Enviamos orientações no agendamento. A preparação do imóvel é responsabilidade do cliente; a equipe não move móveis nem objetos.",
  },
];

const PLANO_MSG = "Olá! Tenho uma imobiliária e quero saber mais sobre o plano mensal da D-Rec.";
const OFERTA_MSG =
  "Olá! Quero aproveitar a oferta de entrada da D-Rec: pacote Imóvel pelo preço do Aéreo.";

function Services() {
  usePageTitle("Pacotes e preços — D-Rec Produções");

  return (
    <>
      {/* 5.1 Cabeçalho */}
      <section className="services-hero">
        <div className="section-head">
          <span className="eyebrow">Pacotes e preços</span>
          <h1 className="section-title">Preço claro. Sem surpresa no orçamento.</h1>
          <p className="services-intro">
            Três pacotes para imóveis avulsos, planos mensais para imobiliárias e extras sob
            medida. Todos incluem deslocamento dentro da {SITE.areaAtendimento}.
          </p>
        </div>
      </section>

      {/* 5.2 Pacotes */}
      <section className="services-section">
        <div className="container packages-grid">
          {PACOTES.map((p) => (
            <article
              key={p.nome}
              className={`package ${p.destaque ? "package--featured" : ""}`}
            >
              {p.destaque && <span className="package-badge">Mais pedido</span>}
              <span className="card-tag">Pacote</span>
              <h2 className="package-name">{p.nome}</h2>
              <p className="package-price">
                <span className="package-price-prefix">a partir de</span>
                {p.preco}
              </p>
              <ul className="package-list">
                {p.itens.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
              <p className="package-support">{p.apoio}</p>
              <a
                href={whatsappLink(p.msg)}
                target="_blank"
                rel="noreferrer"
                className={`btn ${p.destaque ? "btn-primary" : "btn-ghost"}`}
              >
                {p.botao}
              </a>
            </article>
          ))}
        </div>
        <p className="container packages-note">
          Valores para imóveis na {SITE.areaAtendimento}. Fora da RMR, consulte o deslocamento.
          Prazos de entrega contados em dias úteis, a partir da captação.
        </p>
      </section>

      {/* 5.3 Planos mensais */}
      <section className="services-section">
        <div className="container plans">
          <div className="plans-text">
            <span className="eyebrow">Imobiliárias</span>
            <h2 className="section-title">Plano mensal para imobiliárias</h2>
            <p>
              Para quem anuncia com frequência: preço por imóvel menor, agenda fixa na semana e
              padrão visual único em toda a carteira. Pacote Imóvel completo em cada visita.
            </p>
            <p>
              A partir de R$ 500 por imóvel para imobiliárias com 4 ou mais imóveis por mês.
              Quanto maior o volume, menor o valor por imóvel. Fale conosco e montamos o plano.
            </p>
            <a
              href={whatsappLink(PLANO_MSG)}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              Montar meu plano
            </a>
          </div>
        </div>
      </section>

      {/* 5.4 Extras */}
      <section className="services-section">
        <div className="section-head">
          <span className="eyebrow">Extras</span>
          <h2 className="section-title">Extras sob medida</h2>
        </div>
        <ul className="container extras-list">
          {EXTRAS.map((e) => (
            <li key={e.nome}>
              <span className="extra-name">{e.nome}</span>
              <span className="extra-price">{e.preco}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 5.5 Oferta de entrada */}
      <section className="services-section">
        <div className="container">
          <ViewfinderFrame tag="OFERTA · 1º IMÓVEL" className="offer-box">
            <span className="eyebrow">Oferta de entrada</span>
            <h2 className="section-title">Primeiro imóvel com desconto</h2>
            <p>
              Contrate o pacote Imóvel pelo preço do Aéreo na sua primeira visita: R$ 390 em vez
              de R$ 690. Válido uma vez por corretor ou imobiliária.
            </p>
            <a
              href={whatsappLink(OFERTA_MSG)}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              Quero aproveitar
            </a>
          </ViewfinderFrame>
        </div>
      </section>

      {/* 5.6 Perguntas frequentes */}
      <section className="services-section">
        <div className="section-head">
          <span className="eyebrow">FAQ</span>
          <h2 className="section-title">Perguntas frequentes</h2>
        </div>
        <div className="container faq">
          {FAQ.map((f) => (
            <details key={f.q} className="faq-item">
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* 5.7 CTA final */}
      <CtaFinal />
    </>
  );
}

export default Services;
